/*
Waiting for saves while the user is leaving (XOL-596; Carlos, 2026-10-06).

axiosClient has no request timeout, so a save can HANG. When the user is waiting
on one (closing a drawer, running a command, navigating, logging out), after
HANG_MS with no answer they are told the changes have NOT been saved yet and
choose:

  Keep waiting   (primary) keep waiting; asked again after another interval
  Leave now      (or "Cancel" for a command) stop waiting

The dialog lists the fields still unsaved, with a Copy for each, as the logout
dialog does. One reusable helper: drawer close (slice 2), commands (slice 3,
second button "Cancel") and navigation / logout (slice 5) all call it.

    waitForSaves(root, flushPromise, { leaveLabel, intervalMs, pending, noListText })
      -> Promise<{ left: Boolean, result }>

`left: false` means the saves settled (`result` is their outcome); `left: true`
means the user chose to stop waiting (the saves are still running). Never
rejects.

A different failure, a close or navigation blocked because the saves FAIL on the
network or with a 5xx (Carlos, 2026-10-08), is `keepTrying` below: the failed fields
are retried with a back-off, and after 30 s of continuous failure the user is asked
whether to keep trying or close without saving.

    keepTrying(root, failedResult, attempt, { pending, abandon, alive, promptMs, delaysMs })
      -> Promise<{ close: Boolean, abandoned: Boolean, saved: Boolean }>

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import * as sync from '@/lib/requestRowSync'
import StaleSaveNotice from '@/components/requests/StaleSaveNotice'
import { copyText } from '@/lib/requestSyncCopy'
import { groupsFor } from '@/lib/syncNotices'
import * as savePopup from '@/lib/savePopup'

// How long a save may stay unanswered before the user is asked.
export var HANG_MS = 15000

function dialogBody (root, pending, noListText) {
  var h = root.$createElement
  var items = itemsOf(pending)

  if (items.length === 0) {
    return h(StaleSaveNotice, { props: { lead: noListText || copyText('requestSync.hang.leaveNoList') } })
  }

  return h(StaleSaveNotice, {
    props: {
      groups: groupsFor(root, items),
      lead: copyText('requestSync.hang.lead', { n: items.length }),
      footer: copyText('requestSync.hang.leaveWarning', { n: items.length })
    }
  })
}

function itemsOf (pending) {
  return pending.map(function (p) {
    return { kind: p.meta.kind, id: p.meta.id, path: p.path, value: p.yours, meta: p.meta }
  })
}

export function waitForSaves (root, flushPromise, opts) {
  opts = opts || {}
  var interval = typeof opts.intervalMs === 'number' ? opts.intervalMs : HANG_MS
  var leaveLabel = opts.leaveLabel || copyText('requestSync.hang.leaveNow')

  return new Promise(function (resolve) {
    var finished = false
    var timer = null
    var open = false

    var finish = function (value) {
      if (finished) {
        return
      }
      finished = true
      clearTimeout(timer)
      if (open) {
        try { root.$msgbox.close() } catch (e) { /* already closed */ }
        open = false
      }
      resolve(value)
    }

    var arm = function () {
      clearTimeout(timer)
      timer = setTimeout(ask, interval)
    }

    var ask = function () {
      if (finished) {
        return
      }
      var pending = opts.pending ? opts.pending() : sync.pendingFields()
      open = true

      root.$confirm(dialogBody(root, pending, opts.noListText), copyText('requestSync.hang.title'), {
        confirmButtonText: copyText('requestSync.hang.keepWaiting'),
        cancelButtonText: leaveLabel,
        type: 'warning',
        showClose: false,
        distinguishCancelAndClose: true,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        customClass: 'xol-reload-box'
      }).then(function () {
        // Keep waiting: ask again after another interval.
        open = false
        if (!finished) {
          arm()
        }
      }, function (action) {
        open = false
        if (action === 'cancel') {
          finish({ left: true, result: null })
        } else if (!finished) {
          arm()
        }
      })
    }

    Promise.resolve(flushPromise).then(function (result) {
      finish({ left: false, result: result })
    }, function (e) {
      console.error('saveWait: the saves rejected', e)
      finish({ left: false, result: { ok: false, failed: [], results: [] } })
    })

    arm()
  })
}

/* ------------------------------------------------------------------------
   A blocked close / navigation during a connection problem
------------------------------------------------------------------------ */

// Continuous failure, counted from the first failed attempt, before the user is
// asked (and again after each "Keep trying").
export var CONNECTION_PROMPT_MS = 30000

// While the question is open, how often to check that the drawer it is about still
// exists (a destroyed drawer closes the question).
export var ALIVE_POLL_MS = 500

// Pause before each automatic retry of the failed fields; the last one repeats.
// Each request already carries axios-retry's own attempts, so the first pause is
// not short.
export var RETRY_DELAYS_MS = [2000, 4000, 8000, 10000]

/**
 * Is this flush result a connection problem and nothing else? Every failed save
 * failed on the network or with a 5xx (outcome status 'network'). A 409 / 403 / 422
 * (or anything else) in the mix makes it a failure with its own notice, and the
 * drawer stays open as before.
 */
export function isConnectionProblem (result) {
  var failed = result && result.failed
  return !!failed && failed.length > 0 && failed.every(function (o) {
    return !!o && o.status === 'network'
  })
}

function connectionBody (root, pending, seconds) {
  var h = root.$createElement
  var items = itemsOf(pending)

  return h(StaleSaveNotice, {
    props: {
      groups: items.length ? groupsFor(root, items) : [],
      lead: copyText('requestSync.connection.lead', { n: seconds })
    }
  })
}

/**
 * The saves of a close (or an in-app navigation) failed because the server could not
 * be reached. Keep the user where they are, retry the failed fields on a back-off
 * (`attempt()` runs one more flush and returns its promise), and after 30 s of
 * continuous failure ask: Keep trying (primary; asked again after another 30 s) or
 * Close without saving (danger; `abandon()` drops the pending fields so nothing is
 * sent later).
 *
 * Resolves { close, abandoned, saved }:
 *   saved: true   the pending fields WERE saved (a retry succeeded, or someone else's
 *                 retry saved them): the close may go on to ask about the approval
 *                 lock. false for Close without saving and for the hang dialog's
 *                 Leave now, which give up the changes (no lock prompt then).
 *   close: true   a retry succeeded, or the user chose Close without saving, or the
 *                 hang dialog's Leave now
 *   close: false  something other than a connection problem blocks the close (it has
 *                 its own notice), or `alive()` went false: the caller keeps things
 *                 open as it did before this existed
 *
 * `failedResult` that is not a connection problem resolves { close: false } at once.
 * An attempt that hangs gets the usual 15 s dialog (waitForSaves); the question is
 * never opened over it, nor over itself. If `alive()` turns false while the question
 * is open, the question is closed and the result is close:false. Never rejects.
 *
 * @param {Object}   root
 * @param {Object}   failedResult  the flush result that blocked the close
 * @param {Function} attempt       () => Promise<flush result>, one more flush
 * @param {Object}   opts          { pending: () => [pendingFields], abandon: () => void,
 *                                   alive: () => Boolean, promptMs, delaysMs }
 */
export function keepTrying (root, failedResult, attempt, opts) {
  opts = opts || {}
  var promptMs = typeof opts.promptMs === 'number' ? opts.promptMs : CONNECTION_PROMPT_MS
  var delays = opts.delaysMs || RETRY_DELAYS_MS
  var listPending = opts.pending || function () { return sync.pendingFields() }

  if (!isConnectionProblem(failedResult)) {
    return Promise.resolve({ close: false, abandoned: false, saved: false })
  }

  return new Promise(function (resolve) {
    var finished = false
    var promptTimer = null
    var watchTimer = null
    var retryTimer = null
    var attempting = false
    var due = false
    var asked = 0
    var tries = 0
    var attemptNo = 0

    var finish = function (value) {
      if (finished) {
        return
      }
      finished = true
      clearTimeout(promptTimer)
      clearTimeout(retryTimer)
      clearInterval(watchTimer)
      // The loop is over: the strip goes, and so does the connection-problem section
      // (a retry saved, the user gave up, or something else now blocks the close).
      savePopup.setRetry(null)
      savePopup.resolveNetwork()
      resolve(value)
    }

    var stopped = function () {
      return finished || (typeof opts.alive === 'function' && !opts.alive())
    }

    var armPrompt = function () {
      clearTimeout(promptTimer)
      promptTimer = setTimeout(function () {
        if (attempting) {
          due = true
        } else {
          ask()
        }
      }, promptMs)
    }

    var schedule = function () {
      clearTimeout(retryTimer)
      var delay = delays[Math.min(tries, delays.length - 1)]
      tries++
      // The card's strip counts down to this attempt; Try again skips the wait.
      savePopup.setRetry({ attempt: attemptNo + 1, nextAt: Date.now() + delay, running: false }, runNow)
      retryTimer = setTimeout(run, delay)
    }

    var runNow = function () {
      if (finished || attempting) {
        return
      }
      clearTimeout(retryTimer)
      run()
    }

    var run = function () {
      if (stopped()) {
        finish({ close: false, abandoned: false, saved: false })
        return
      }
      attempting = true
      attemptNo++
      savePopup.setRetry({ attempt: attemptNo, running: true })
      // A throwing attempt is a rejected promise, which waitForSaves reads as a
      // failure that is not a connection problem.
      var flush = Promise.resolve().then(attempt)
      waitForSaves(root, flush, { pending: listPending }).then(function (r) {
        attempting = false
        if (finished) {
          return
        }
        if (r.left) {
          // The hang dialog's Leave now: that dialog listed what is lost.
          finish({ close: true, abandoned: false, saved: false })
        } else if (r.result && r.result.ok) {
          finish({ close: true, abandoned: false, saved: true })
        } else if (!isConnectionProblem(r.result)) {
          finish({ close: false, abandoned: false, saved: false })
        } else if (due) {
          due = false
          ask()
        } else {
          schedule()
        }
      })
    }

    var ask = function () {
      if (stopped()) {
        finish({ close: false, abandoned: false, saved: false })
        return
      }
      var pending = listPending()
      if (pending.length === 0) {
        // Saved by someone else's retry (the notice's Try again) in the meantime.
        finish({ close: true, abandoned: false, saved: true })
        return
      }
      clearTimeout(retryTimer)
      savePopup.setRetry(null)
      asked++

      // The drawer can go away while the question is open (alive() turns false):
      // then the question is about nothing, so it closes with it.
      if (typeof opts.alive === 'function') {
        watchTimer = setInterval(function () {
          if (!opts.alive()) {
            clearInterval(watchTimer)
            try { root.$msgbox.close() } catch (e) { /* already closed */ }
            finish({ close: false, abandoned: false, saved: false })
          }
        }, ALIVE_POLL_MS)
      }

      root.$confirm(connectionBody(root, pending, asked * Math.round(promptMs / 1000)), copyText('requestSync.connection.title'), {
        confirmButtonText: copyText('requestSync.connection.keepTrying'),
        cancelButtonText: copyText('requestSync.connection.closeWithout'),
        cancelButtonClass: 'el-button--danger',
        type: 'warning',
        showClose: false,
        distinguishCancelAndClose: true,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        customClass: 'xol-reload-box xol-connection-box'
      }).then(function () {
        clearInterval(watchTimer)
        if (!finished) {
          armPrompt()
          run()
        }
      }, function (action) {
        clearInterval(watchTimer)
        if (finished) {
          return
        }
        if (action === 'cancel') {
          if (typeof opts.abandon === 'function') {
            opts.abandon()
          }
          finish({ close: true, abandoned: true, saved: false })
        } else {
          // Esc and the X do not close without saving: they keep trying.
          armPrompt()
          run()
        }
      })
    }
    armPrompt()
    schedule()
  })
}
